
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/ScrollToTop.jsx';
import CookieBanner from './components/CookieBanner.jsx';
import HomePage from './pages/HomePage.jsx';
import { Loader2 } from 'lucide-react';
import { I18nProvider } from '@/i18n/index.jsx';

// Lazy load dynamic pages (HomePage stays eager as the landing page)
const AboutPage = React.lazy(() => import('./pages/AboutPage.jsx'));
const ContactPage = React.lazy(() => import('./pages/ContactPage.jsx'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy.jsx'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService.jsx'));
const LegalNotice = React.lazy(() => import('./pages/LegalNotice.jsx'));
const DataProtection = React.lazy(() => import('./pages/DataProtection.jsx'));
const CookiesPolicy = React.lazy(() => import('./pages/CookiesPolicy.jsx'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage.jsx'));
const ResourcesPage = React.lazy(() => import('./pages/ResourcesPage.jsx'));
const SitemapPage = React.lazy(() => import('./pages/SitemapPage.jsx'));
const RobotsPage = React.lazy(() => import('./pages/RobotsPage.jsx'));
const UnitPairPage = React.lazy(() => import('./pages/UnitPairPage.jsx'));
const IMCPage = React.lazy(() => import('./pages/IMCPage.jsx'));
const ShoeSize = React.lazy(() => import('./pages/ShoeSize.jsx'));
const ArticlePage = React.lazy(() => import('@/pages/ArticlePage.jsx'));
const SeoDiagnosticoPage = React.lazy(() => import('@/pages/SeoDiagnosticoPage.jsx'));
const ConverterPage = React.lazy(() => import('./pages/ConverterPage.jsx'));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage.jsx'));

function App() {
  return (
    <HelmetProvider>
      <Router>
        <I18nProvider>
          <ScrollToTop />
          <CookieBanner />
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-background">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          }>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/sobre" element={<AboutPage />} />
              <Route path="/contato" element={<ContactPage />} />
              <Route path="/recursos" element={<ResourcesPage />} />
              <Route path="/calculadora/imc" element={<IMCPage />} />
              <Route path="/conversor/calcado" element={<ShoeSize />} />
              
              {/* Specific Pair Route MUST be before the generic ID route */}
              <Route path="/conversor/:converterId/:unitPairId" element={<UnitPairPage />} />
              
              {/* Dynamic Category/Converter Routes */}
              <Route path="/conversor/:id" element={<ConverterPage />} />
              <Route path="/categoria/:id" element={<CategoryPage />} />
              
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/legal-notice" element={<LegalNotice />} />
              <Route path="/data-protection" element={<DataProtection />} />
              <Route path="/cookies-policy" element={<CookiesPolicy />} />

              {/* Article Routes */}
              <Route path="/recursos/:slug" element={<ArticlePage />} />
              <Route path="/artigos/:slug" element={<ArticlePage />} />

              {/* Mirrored English Routes */}
              <Route path="/en" element={<HomePage />} />
              <Route path="/en/sobre" element={<AboutPage />} />
              <Route path="/en/contato" element={<ContactPage />} />
              <Route path="/en/recursos" element={<ResourcesPage />} />
              <Route path="/en/calculadora/imc" element={<IMCPage />} />
              <Route path="/en/conversor/calcado" element={<ShoeSize />} />
              <Route path="/en/conversor/:converterId/:unitPairId" element={<UnitPairPage />} />
              <Route path="/en/conversor/:id" element={<ConverterPage />} />
              <Route path="/en/categoria/:id" element={<CategoryPage />} />
              <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/en/terms-of-service" element={<TermsOfService />} />
              <Route path="/en/recursos/:slug" element={<ArticlePage />} />
              <Route path="/en/artigos/:slug" element={<ArticlePage />} />

              {/* SEO Routes */}
              <Route path="/sitemap.xml" element={<SitemapPage />} />
              <Route path="/robots.txt" element={<RobotsPage />} />

              {/* Diagnóstico GSC interno (noindex) — sem alteração de SEO público */}
              <Route path="/seo-diagnostico" element={<SeoDiagnosticoPage />} />
              <Route path="/seo-diagnostico/" element={<SeoDiagnosticoPage />} />
              
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </I18nProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
